import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Volume2, VolumeX } from "lucide-react";

export default function Intro() {
  const videoRef = useRef(null);
  const nav = useNavigate();
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(true);

  const enter = () => nav("/home");

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) setEnded(true);
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
  }, []);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (v.paused) v.play().catch(() => {});
    setMuted(v.muted);
  };

  return (
    <div className="fixed inset-0 bg-black overflow-hidden" data-testid="intro-page">
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        src="/intro_web.mp4"
        poster="/intro_poster.jpg"
        autoPlay
        muted={muted}
        playsInline
        onEnded={() => setEnded(true)}
        data-testid="intro-video"
      />

      {/* subtle vignette */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 pointer-events-none" />

      {/* Sound toggle */}
      <button
        onClick={toggleSound}
        data-testid="intro-sound"
        className="absolute top-6 left-6 z-20 w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
        aria-label="Toggle sound"
      >
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>

      {/* Skip while playing */}
      <AnimatePresence>
        {!ended && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 1.2 }}
            onClick={enter}
            data-testid="intro-skip"
            className="absolute top-6 right-6 z-20 font-mono text-[11px] uppercase tracking-[0.25em] text-white/70 hover:text-white transition-colors flex items-center gap-2"
          >
            Skip intro <ArrowRight size={14} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Enter site at end */}
      <AnimatePresence>
        {ended && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 z-20 flex flex-col items-center justify-end pb-24 md:pb-32"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.7 }}
              className="flex flex-col items-center"
            >
              <img src="/logo.png" alt="MARKIE 2 FRESH" className="h-12 md:h-16 w-auto mb-8 drop-shadow-[0_10px_30px_rgba(0,0,0,0.5)]" />
              <button
                onClick={enter}
                data-testid="enter-site"
                className="group relative inline-flex items-center gap-3 rounded-full bg-brand-orange text-white pl-9 pr-3 py-3.5 overflow-hidden"
              >
                <span className="absolute inset-0 bg-white scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
                <span className="relative font-mono text-sm uppercase tracking-[0.25em] group-hover:text-brand-ink transition-colors">
                  Enter Site Here
                </span>
                <span className="relative w-10 h-10 rounded-full bg-white group-hover:bg-brand-orange flex items-center justify-center transition-colors">
                  <ArrowRight size={18} className="text-brand-orange group-hover:text-white transition-colors" />
                </span>
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
